import { BrowserRouter as Router } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { BrandProvider } from './context/BrandContext';
import { NotificationProvider } from './context/NotificationContext';
import { ThemeProvider as TailwindThemeProvider, useTheme as useTailwindTheme } from './context/ThemeContext';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import AppRouter from './router/AppRouter';
import ErrorBoundary from './components/ErrorBoundary';
import PWAInstallPrompt from './components/pwa/PWAInstallPrompt';
import OfflineIndicator from './components/pwa/OfflineIndicator';
import RateLimitNotification from './components/RateLimitNotification';
import PlanLimitModal from './components/PlanLimitModal';
import ToastContainer from './components/Toast';
import { OnboardingWizard } from './components/onboarding';
import { useOnboarding } from './hooks/useOnboarding';
import { FeedbackWidget } from './components/feedback';
import { usePrefetchStrategy } from './utils/prefetch.jsx';
import './styles/accessibility.css';
import './styles/animations.css';

function AppContent() {
  const { isDark } = useTailwindTheme();
  const { user, isAuthenticated } = useAuth();
  
  // Use new production-ready onboarding hook
  const onboarding = useOnboarding({ userId: user?.id });

  // Use the isDark value from context (safe, no direct window access)
  const isDarkMode = isDark;

  // Create MUI theme based on dark mode
  const muiTheme = createTheme({
    palette: {
      mode: isDarkMode ? 'dark' : 'light',
      ...(isDarkMode ? {
        // Dark mode palette
        background: {
          default: '#121212',
          paper: '#1e1e1e',
        },
        text: {
          primary: '#ffffff',
          secondary: '#b0b0b0',
        },
        divider: '#424242',
        primary: {
          main: '#90caf9',
        },
        error: {
          main: '#f44336',
        },
        warning: {
          main: '#ffc107',
        },
        info: {
          main: '#2196f3',
        },
        success: {
          main: '#4caf50',
        },
      } : {
        // Light mode palette
        background: {
          default: '#ffffff',
          paper: '#ffffff',
        },
        text: {
          primary: '#000000',
          secondary: '#666666',
        },
      }),
    },
  });

  // Prefetch routes based on user navigation
  usePrefetchStrategy();

  return (
    <MuiThemeProvider theme={muiTheme}>
      <CssBaseline />
      <>
        <AppRouter />
        <PWAInstallPrompt />
        <OfflineIndicator />
        <RateLimitNotification />
        <PlanLimitModal />
        <ToastContainer />
        {isAuthenticated && <OnboardingWizard onboarding={onboarding} />}
        {isAuthenticated && <FeedbackWidget position="bottom-right" />}
      </>
    </MuiThemeProvider>
  );
}

function App() {
  return (
    <ErrorBoundary>
      <TailwindThemeProvider>
        <AuthProvider>
          <BrandProvider>
            <NotificationProvider>
              <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
                <AppContent />
              </Router>
            </NotificationProvider>
          </BrandProvider>
        </AuthProvider>
      </TailwindThemeProvider>
    </ErrorBoundary>
  );
}

export default App;
